import { createApp } from "vue";
import { createPinia } from "pinia";
import * as bootstrap from 'bootstrap';
import App from "./App.vue";
import router from "./router";
import { bootDiag } from "@/core/helpers/bootDiag";

// ── Lazy-chunk load failure recovery ─────────────────────────────────────────
// Vite emits a `vite:preloadError` event when a dynamically-imported chunk fails
// to load — typically after a new deploy (the cached index.html references chunk
// hashes that no longer exist) or a transient network error. Escalating stages,
// each guarded by a sessionStorage timestamp (a boolean cleared on `window.load`
// was useless: `load` fires on every page load, before the lazy chunk request,
// so the flag never survived the reload and a persistent failure looped forever):
//
//   1. Plain reload — pulls the fresh index.html + chunk graph.
//   2. If the failure recurs within the cooldown, the reload didn't heal it.
//      That's the service-worker case: workbox's navigateFallback answers the
//      reload with the same stale PRECACHED index.html, so no number of plain
//      reloads ever reaches the server. Unregister all SWs, drop all caches,
//      then reload once more — now the network serves the current build.
//   3. Still failing → give up WITHOUT preventDefault(): a consumed event makes
//      Vite swallow the error and the import resolves `undefined`, which
//      vue-router reports as the opaque `Couldn't resolve component "default"`
//      blank screen. Propagating instead surfaces the real chunk error to
//      router.onError/ErrorLogService.
const PRELOAD_RELOAD_KEY = "vite-preload-reloaded-at";
const PRELOAD_PURGE_KEY = "vite-preload-purged-at";
const PRELOAD_RELOAD_COOLDOWN_MS = 15000;
window.addEventListener("vite:preloadError", (event) => {
  const now = Date.now();

  // Der Reload loescht jede Spur im Konsolenfenster. Ohne diese Zeile ist von
  // aussen nicht zu unterscheiden, ob die Seite wegen eines fehlenden Chunks
  // neu geladen hat oder aus einem ganz anderen Grund — gemeldet wird beides
  // als „das Frontend laedt neu". Der Ringpuffer ueberlebt den Reload.
  bootDiag('preload.failed', (event as any)?.payload?.message ?? (event as any)?.payload ?? 'chunk load failed');

  const lastReload = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY) || 0);
  if (now - lastReload >= PRELOAD_RELOAD_COOLDOWN_MS) {
    event.preventDefault();
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(now));
    bootDiag('preload.reload', 'stage 1 — plain reload');
    window.location.reload();
    return;
  }

  const lastPurge = Number(sessionStorage.getItem(PRELOAD_PURGE_KEY) || 0);
  if (now - lastPurge >= PRELOAD_RELOAD_COOLDOWN_MS) {
    event.preventDefault();
    sessionStorage.setItem(PRELOAD_PURGE_KEY, String(now));
    const cleanup: Promise<unknown>[] = [];
    if ("serviceWorker" in navigator) {
      cleanup.push(
        navigator.serviceWorker
          .getRegistrations()
          .then((regs) => Promise.allSettled(regs.map((r) => r.unregister())))
      );
    }
    if ("caches" in window) {
      cleanup.push(
        caches
          .keys()
          .then((keys) => Promise.allSettled(keys.map((k) => caches.delete(k))))
      );
    }
    bootDiag('preload.purge-reload', 'stage 2 — SW + caches dropped');
    Promise.allSettled(cleanup).then(() => window.location.reload());
    return;
  }
  // Stage 3: no preventDefault → Vite rethrows the original import error.
});

// ── Dev-only service worker cleanup ──────────────────────────────────────────
// devOptions.enabled was briefly true in vite.config.ts, which installed a
// workbox SW on dev origins (localhost:8086). That SW keeps serving stale
// cached chunks and re-activating, causing an endless auto-reload loop even
// after devOptions is disabled — a registered SW survives config changes.
// Unregister it and drop its caches so dev browsers heal themselves.
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((r) => r.unregister());
  });
  if ("caches" in window) {
    caches.keys().then((keys) => keys.forEach((key) => caches.delete(key)));
  }
}

const pinia = createPinia()
const app = createApp(App)

app.use(pinia)
app.use(router);

// ── Global error capture — must be before any component mounts ───────────────
import ErrorLogService from "@/core/services/ErrorLogService";
ErrorLogService.init(app);

//imports for app initialization
import ApiService from "@/core/services/ApiService";
import DBConfigService from "@/core/services/DBConfigService";
import { initApexCharts } from "@/core/plugins/apexcharts";
import { initInlineSvg } from "@/core/plugins/inline-svg";
import { initVeeValidate } from "@/core/plugins/vee-validate";
import { initKtIcon } from "@/core/plugins/keenthemes";
import { createGlobalComponents } from "@/core/components/components";
import { vInteract } from "@/core/directives/vInteract";
import { vDropZone } from "@/core/directives/vDropZone";
import { vBackdropClose } from "@/core/directives/vBackdropClose";
import i18n from "@/core/plugins/i18n/i18n";
import { initLocaleBridge } from "@/core/plugins/i18n/localeBridge";
import '@/core/helpers/date';
import { initNative } from "@/core/native";
// prismjs is lazy-loaded by components that need it — not imported globally

ApiService.init(app);
createGlobalComponents(app);
initApexCharts(app);
initInlineSvg(app);
initKtIcon(app);
initVeeValidate();

app.use(i18n);
// Eine Quelle für die Sprache: user.settings.language speist Oberfläche UND
// Inhalte. Braucht ein aktives Pinia (oben) — deshalb erst hier.
initLocaleBridge();

app.directive("tooltip", (el) => {
  new bootstrap.Tooltip(el);
});

app.directive("interact", vInteract);
app.directive("drop-zone", vDropZone);
app.directive("backdrop-close", vBackdropClose);

// Native Huelle (Capacitor: iOS/macOS) aufsetzen, BEVOR die erste View
// rendert — Statusleiste, Safe-Area-Klasse und die Umleitung externer Links
// muessen stehen, sonst blitzt beim Start ein falsches Layout auf.
// Im Browser ist der Aufruf ein No-op.
initNative();

app.mount("#app");
